import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';

import { functions, storage } from '@/modules/firebase';

export type RecordFileType = 'shipments' | 'ncr' | 'issues';
export type RecordFileStatus = 'pending' | 'ready' | 'deleted';

export interface RecordFile {
  id: string;
  recordType: RecordFileType;
  recordId: string;
  displayName: string;
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  status: RecordFileStatus;
  deleted: boolean;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  storagePath: string;
  version: number;
}

export interface CreateFileUploadSessionPayload {
  recordType: RecordFileType;
  recordId: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  displayName?: string;
  fileId?: string;
}

export interface CreateFileUploadSessionResult {
  file: RecordFile;
  uploadPath: string;
  maxSizeBytes: number;
}

interface CallableEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

interface RawRecordFile {
  id: string;
  recordType: RecordFileType;
  recordId: string;
  displayName: string;
  originalName: string;
  mimeType: string;
  extension: string;
  sizeBytes: number;
  status: RecordFileStatus;
  deleted: boolean;
  createdBy: string;
  createdAt: unknown;
  updatedAt: unknown;
  storagePath: string;
  version: number;
}

function timestampToMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof maybeTimestamp.toMillis === 'function') {
      return maybeTimestamp.toMillis();
    }

    const seconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds;
    const nanoseconds = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return (seconds * 1000) + Math.floor(nanoseconds / 1_000_000);
    }
  }

  return 0;
}

function normalizeFile(raw: RawRecordFile): RecordFile {
  return {
    ...raw,
    createdAt: timestampToMillis(raw.createdAt),
    updatedAt: timestampToMillis(raw.updatedAt)
  };
}

export class FileClient {
  private readonly createFileUploadSessionCallable = httpsCallable<any, CallableEnvelope<CreateFileUploadSessionResult & { file: RawRecordFile }>>(functions, 'createFileUploadSession');
  private readonly replaceFileVersionCallable = httpsCallable<any, CallableEnvelope<CreateFileUploadSessionResult & { file: RawRecordFile }>>(functions, 'replaceFileVersion');
  private readonly finalizeFileUploadCallable = httpsCallable<any, CallableEnvelope<RawRecordFile>>(functions, 'finalizeFileUpload');
  private readonly updateFileCallable = httpsCallable<any, CallableEnvelope<RawRecordFile>>(functions, 'updateFile');
  private readonly deleteFileCallable = httpsCallable<any, CallableEnvelope<unknown>>(functions, 'deleteFile');

  private unwrapEnvelope<T>(envelope: CallableEnvelope<T> | undefined, fallbackMessage: string): T {
    if (!envelope?.success) {
      throw new Error(envelope?.message || fallbackMessage);
    }

    return envelope.data as T;
  }

  async createUploadSession(payload: CreateFileUploadSessionPayload): Promise<CreateFileUploadSessionResult> {
    const result = await this.createFileUploadSessionCallable(payload);
    const session = this.unwrapEnvelope(result.data, 'Failed to create file upload session');

    return {
      ...session,
      file: normalizeFile(session.file)
    };
  }

  async replaceFileVersion(payload: CreateFileUploadSessionPayload & { fileId: string }): Promise<CreateFileUploadSessionResult> {
    const result = await this.replaceFileVersionCallable(payload);
    const session = this.unwrapEnvelope(result.data, 'Failed to create file replacement session');

    return {
      ...session,
      file: normalizeFile(session.file)
    };
  }

  async finalizeFileUpload(recordType: RecordFileType, recordId: string, fileId: string): Promise<RecordFile> {
    const result = await this.finalizeFileUploadCallable({ recordType, recordId, fileId });
    return normalizeFile(this.unwrapEnvelope(result.data, 'Failed to finalize file upload'));
  }

  async updateFile(recordType: RecordFileType, recordId: string, fileId: string, displayName: string): Promise<RecordFile> {
    const result = await this.updateFileCallable({ recordType, recordId, fileId, displayName });
    return normalizeFile(this.unwrapEnvelope(result.data, 'Failed to update file'));
  }

  async deleteFile(recordType: RecordFileType, recordId: string, fileId: string): Promise<void> {
    const result = await this.deleteFileCallable({ recordType, recordId, fileId });
    this.unwrapEnvelope(result.data, 'Failed to delete file');
  }

  async getDownloadUrl(storagePath: string): Promise<string> {
    return getDownloadURL(storageRef(storage, storagePath));
  }
}

export const fileClient = new FileClient();